import { partnerPricingBillingMode } from '../../api.js';
import type {
  PlatformProviderModelDeployment,
  PlatformProviderModelRateInput,
} from '../../types.js';

export interface ManualProviderRateFormValues {
  inputRate: string;
  outputRate: string;
  cachedInputRate: string;
  cacheWriteInputRate: string;
  marginMultiplier: string;
  /** Carried from the current rate so a republish keeps its long-context tiers. */
  promptTiers?: PlatformProviderModelRateInput['promptTiers'];
}

export function manualProviderActiveDeployment(
  deployments: PlatformProviderModelDeployment[] | undefined,
  tenantId?: string
): PlatformProviderModelDeployment | undefined {
  const activeDeployments =
    deployments?.filter((deployment) => deployment.status === 'active') ?? [];
  if (tenantId) {
    return activeDeployments.find((deployment) => deployment.tenantId === tenantId);
  }
  return activeDeployments.length === 1 ? activeDeployments[0] : undefined;
}

export function manualProviderRateFormValues(
  deployment?: PlatformProviderModelDeployment
): ManualProviderRateFormValues {
  if (deployment?.currentRate) {
    return {
      inputRate: String(deployment.currentRate.inputUsdPerMillion),
      outputRate: String(deployment.currentRate.outputUsdPerMillion),
      cachedInputRate:
        deployment.currentRate.cachedInputUsdPerMillion === undefined
          ? ''
          : String(deployment.currentRate.cachedInputUsdPerMillion),
      cacheWriteInputRate:
        deployment.currentRate.cacheWriteInputUsdPerMillion === undefined
          ? ''
          : String(deployment.currentRate.cacheWriteInputUsdPerMillion),
      marginMultiplier: String(deployment.currentRate.marginMultiplier),
      ...(deployment.currentRate.promptTiers?.length
        ? { promptTiers: deployment.currentRate.promptTiers }
        : {}),
    };
  }
  if (deployment && !deployment.rateVersion) {
    return { inputRate: '0', outputRate: '0', cachedInputRate: '', cacheWriteInputRate: '', marginMultiplier: '1' };
  }
  return { inputRate: '', outputRate: '', cachedInputRate: '', cacheWriteInputRate: '', marginMultiplier: '1' };
}

export type ManualProviderRateError =
  'required' | 'not_a_number' | 'negative' | 'mixed_zero' | 'free_cache_rate' | 'cached_input_above_input' | 'margin_not_positive';

export type ManualProviderRateState =
  | { kind: 'invalid'; error: ManualProviderRateError }
  | {
      kind: 'free' | 'paid';
      rate: Pick<
        PlatformProviderModelRateInput,
        | 'inputUsdPerMillion'
        | 'outputUsdPerMillion'
        | 'cachedInputUsdPerMillion'
        | 'cacheWriteInputUsdPerMillion'
        | 'marginMultiplier'
        | 'promptTiers'
      >;
    };

function requiredNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function manualProviderRateState(input: ManualProviderRateFormValues): ManualProviderRateState {
  if (!input.inputRate.trim() || !input.outputRate.trim()) {
    return { kind: 'invalid', error: 'required' };
  }
  const inputUsdPerMillion = requiredNumber(input.inputRate);
  const outputUsdPerMillion = requiredNumber(input.outputRate);
  if (inputUsdPerMillion === undefined || outputUsdPerMillion === undefined) {
    return { kind: 'invalid', error: 'not_a_number' };
  }
  if (inputUsdPerMillion < 0 || outputUsdPerMillion < 0) {
    return { kind: 'invalid', error: 'negative' };
  }
  if ((inputUsdPerMillion === 0) !== (outputUsdPerMillion === 0)) {
    return { kind: 'invalid', error: 'mixed_zero' };
  }
  const cachedInputUsdPerMillion = requiredNumber(input.cachedInputRate);
  const cacheWriteInputUsdPerMillion = requiredNumber(input.cacheWriteInputRate);
  if (
    (input.cachedInputRate.trim() && cachedInputUsdPerMillion === undefined) ||
    (input.cacheWriteInputRate.trim() && cacheWriteInputUsdPerMillion === undefined)
  ) {
    return { kind: 'invalid', error: 'not_a_number' };
  }
  if (
    (cachedInputUsdPerMillion !== undefined && cachedInputUsdPerMillion < 0) ||
    (cacheWriteInputUsdPerMillion !== undefined && cacheWriteInputUsdPerMillion < 0)
  ) {
    return { kind: 'invalid', error: 'negative' };
  }
  const rates = { inputUsdPerMillion, outputUsdPerMillion };
  if (partnerPricingBillingMode(rates) === 'byo-unbilled') {
    if (cachedInputUsdPerMillion !== undefined || cacheWriteInputUsdPerMillion !== undefined) {
      return { kind: 'invalid', error: 'free_cache_rate' };
    }
    return { kind: 'free', rate: rates };
  }
  if (cachedInputUsdPerMillion !== undefined && cachedInputUsdPerMillion > inputUsdPerMillion) {
    return { kind: 'invalid', error: 'cached_input_above_input' };
  }
  const marginMultiplier = requiredNumber(input.marginMultiplier);
  if (marginMultiplier === undefined || marginMultiplier <= 0) {
    return { kind: 'invalid', error: 'margin_not_positive' };
  }
  return {
    kind: 'paid',
    rate: {
      ...rates,
      ...(cachedInputUsdPerMillion !== undefined ? { cachedInputUsdPerMillion } : {}),
      ...(cacheWriteInputUsdPerMillion !== undefined ? { cacheWriteInputUsdPerMillion } : {}),
      marginMultiplier,
      ...(input.promptTiers?.length ? { promptTiers: input.promptTiers } : {}),
    }
  };
}
